import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, IconButton, Tooltip } from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import { useSnackbar } from 'notistack';
import api from '../../services/api';

const MAX_RECORD_MS = 5 * 60 * 1000;

const errText = (e: any, fallback: string): string => {
  const d = e?.response?.data;
  return (d && typeof d === 'object' && d.message) || fallback;
};

const pickMime = (): string => {
  const MR: any = (window as any).MediaRecorder;
  if (!MR || typeof MR.isTypeSupported !== 'function') return '';
  return ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'].find((t) => MR.isTypeSupported(t)) || '';
};

const extFor = (type: string): string => (type.includes('mp4') ? 'm4a' : type.includes('ogg') ? 'ogg' : 'webm');

/** Only one reading plays at a time across the app. */
let current: { audio: HTMLAudioElement; url: string; stop: () => void } | null = null;
const stopCurrent = () => {
  if (current) current.stop();
};

type MicProps = {
  /** Called with the recognised text; the caller decides how to add it to its box. */
  onText: (text: string) => void;
  disabled?: boolean;
  size?: 'small' | 'medium';
};

/** Voice typing: click to record, click again to stop; the speech is turned into text by our own Whisper. */
export const MicButton: React.FC<MicProps> = ({ onText, disabled, size = 'small' }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [state, setState] = useState<'idle' | 'recording' | 'working'>('idle');
  const [secs, setSecs] = useState(0);
  const rec = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const timers = useRef<{ tick?: number; limit?: number }>({});
  const stream = useRef<MediaStream | null>(null);

  const cleanup = () => {
    window.clearInterval(timers.current.tick);
    window.clearTimeout(timers.current.limit);
    stream.current?.getTracks().forEach((t) => t.stop());
    stream.current = null;
  };

  useEffect(() => () => {
    if (rec.current && rec.current.state !== 'inactive') {
      rec.current.onstop = null;
      rec.current.stop();
    }
    cleanup();
  }, []);

  const send = async (blob: Blob) => {
    setState('working');
    try {
      const form = new FormData();
      const type = (blob.type || 'audio/webm').split(';')[0];
      form.append('audio', new File([blob], `voice.${extFor(type)}`, { type }));
      const r = await api.post('/speech/transcribe', form, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 300000 });
      const text = String(r.data?.data?.text || '').trim();
      if (text) onText(text);
      else enqueueSnackbar('No words were heard. Please try again.', { variant: 'info' });
    } catch (e) {
      enqueueSnackbar(errText(e, 'Voice typing failed. Please try again.'), { variant: 'error' });
    } finally {
      setState('idle');
    }
  };

  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia || !(window as any).MediaRecorder) {
      enqueueSnackbar('This browser cannot record audio.', { variant: 'warning' });
      return;
    }
    stopCurrent();
    try {
      stream.current = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      enqueueSnackbar('Microphone access was blocked. Allow the microphone for this site and try again.', { variant: 'warning' });
      return;
    }
    const mime = pickMime();
    const r = new MediaRecorder(stream.current, mime ? { mimeType: mime } : undefined);
    chunks.current = [];
    r.ondataavailable = (ev) => { if (ev.data && ev.data.size) chunks.current.push(ev.data); };
    r.onstop = () => {
      cleanup();
      const blob = new Blob(chunks.current, { type: r.mimeType || mime || 'audio/webm' });
      chunks.current = [];
      if (blob.size < 200) {
        setState('idle');
        return;
      }
      send(blob);
    };
    rec.current = r;
    r.start();
    setSecs(0);
    setState('recording');
    timers.current.tick = window.setInterval(() => setSecs((s) => s + 1), 1000);
    timers.current.limit = window.setTimeout(() => { if (r.state !== 'inactive') r.stop(); }, MAX_RECORD_MS);
  };

  const stop = () => {
    if (rec.current && rec.current.state !== 'inactive') rec.current.stop();
  };

  const label = state === 'recording' ? `Stop and type what I said (${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')})`
    : state === 'working' ? 'Turning your speech into text…'
      : 'Voice typing: click, speak, then click again to stop. Your words are added to the box (up to 5 minutes). Runs on our own AI server.';

  return (
    <Tooltip title={label} arrow>
      <span>
        <IconButton
          size={size}
          aria-label={state === 'recording' ? 'Stop recording' : 'Voice typing'}
          color={state === 'recording' ? 'error' : 'default'}
          disabled={disabled || state === 'working'}
          onClick={state === 'recording' ? stop : start}
        >
          {state === 'working' ? <CircularProgress size={18} /> : state === 'recording' ? <StopIcon fontSize="small" /> : <MicIcon fontSize="small" />}
        </IconButton>
      </span>
    </Tooltip>
  );
};

type SpeakProps = {
  /** The text to read; a function is read at click time so it always matches the box. */
  text: string | (() => string);
  disabled?: boolean;
  size?: 'small' | 'medium';
  tip?: string;
};

/** Read aloud with our own text-to-voice model. Click again to stop. */
export const SpeakButton: React.FC<SpeakProps> = ({ text, disabled, size = 'small', tip }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [state, setState] = useState<'idle' | 'loading' | 'playing'>('idle');
  const mine = useRef<{ audio: HTMLAudioElement; url: string; stop: () => void } | null>(null);

  useEffect(() => () => { if (mine.current) mine.current.stop(); }, []);

  const play = useCallback(async () => {
    const value = (typeof text === 'function' ? text() : text || '').trim();
    if (!value) {
      enqueueSnackbar('There is no text to read aloud.', { variant: 'info' });
      return;
    }
    stopCurrent();
    setState('loading');
    try {
      const r = await api.post('/speech/speak', { text: value }, { responseType: 'blob', timeout: 180000 });
      const url = URL.createObjectURL(r.data as Blob);
      const audio = new Audio(url);
      const entry = {
        audio,
        url,
        stop: () => {
          audio.pause();
          URL.revokeObjectURL(url);
          if (current === entry) current = null;
          if (mine.current === entry) mine.current = null;
          setState('idle');
        },
      };
      audio.onended = entry.stop;
      audio.onerror = entry.stop;
      current = entry;
      mine.current = entry;
      await audio.play();
      setState('playing');
    } catch (e: any) {
      let message = 'Reading aloud failed. Please try again.';
      const data = e?.response?.data;
      if (data instanceof Blob) {
        try { message = JSON.parse(await data.text()).message || message; } catch { /* not JSON */ }
      }
      if (mine.current) mine.current.stop();
      setState('idle');
      enqueueSnackbar(message, { variant: 'error' });
    }
  }, [text, enqueueSnackbar]);

  const label = state === 'playing' ? 'Stop reading' : state === 'loading' ? 'Preparing the audio…'
    : (tip || 'Read aloud with our own voice model. Click again to stop.');

  return (
    <Tooltip title={label} arrow>
      <span>
        <IconButton
          size={size}
          aria-label={state === 'playing' ? 'Stop reading' : 'Read aloud'}
          color={state === 'playing' ? 'primary' : 'default'}
          disabled={disabled || state === 'loading'}
          onClick={state === 'playing' ? () => mine.current?.stop() : play}
        >
          {state === 'loading' ? <CircularProgress size={18} /> : state === 'playing' ? <StopCircleIcon fontSize="small" /> : <VolumeUpIcon fontSize="small" />}
        </IconButton>
      </span>
    </Tooltip>
  );
};

/** Adds text from voice typing to an existing box value, with a space or new line as needed. */
export const appendDictation = (prev: string, text: string): string => {
  if (!prev) return text;
  return /\s$/.test(prev) ? `${prev}${text}` : `${prev} ${text}`;
};

type ToolsProps = {
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  speakTip?: string;
};

/** Mic + speaker pair for a text box: voice typing appends to the box, the speaker reads the box aloud. */
const VoiceTools: React.FC<ToolsProps> = ({ value, onChange, disabled, speakTip }) => {
  const latest = useRef(value);
  latest.current = value;
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
      <MicButton disabled={disabled} onText={(t) => onChange(appendDictation(latest.current, t))} />
      <SpeakButton disabled={disabled || !value.trim()} text={() => latest.current} tip={speakTip || 'Read this text aloud with our own voice model. Click again to stop.'} />
    </Box>
  );
};

export default VoiceTools;
